import { getDefaultStore, useAtomValue, useSetAtom } from "jotai";
import { type RefObject, useEffect, useId } from "react";
import { lastFocusedPushToTalkScopeAtom } from "../store/pushToTalkAtoms";

const SCOPE_ATTRIBUTE = "data-push-to-talk-scope";
/**
 * The key is app-wide, so the last-focused composer is too: pinned chat runs
 * under its own Jotai Provider, and a scoped copy of the atom would let it and
 * the ticket's composer both claim one press.
 */
const APP_STORE = { store: getDefaultStore() };

const isEditableTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT");

/** On screen — a collapsed thread panel (0 wide) or a closed drawer (visibility: hidden) is not. */
const isShown = (element: Element): boolean => {
  const { width, height } = element.getBoundingClientRect();
  return (
    width > 0 && height > 0 && getComputedStyle(element).visibility !== "hidden"
  );
};

interface UsePushToTalkScopeOptions {
  /** The composer's root element. */
  ref: RefObject<HTMLElement | null>;
  enabled: boolean;
}

/**
 * Decides which mounted composer a push-to-talk press belongs to, so the key
 * works without clicking into a composer first:
 * 1. focus inside a composer → that composer;
 * 2. focus in any other text field → none (the user is typing elsewhere);
 * 3. otherwise → the visible composer last focused, else the first visible
 *    one on screen (the ticket's main chat, ahead of the thread panel).
 * Every composer applies the same rule to the same press, so exactly one
 * starts. Spread `scopeProps` on the composer's root.
 */
export const usePushToTalkScope = ({
  ref,
  enabled,
}: UsePushToTalkScopeOptions) => {
  const id = useId();
  const lastFocusedId = useAtomValue(lastFocusedPushToTalkScopeAtom, APP_STORE);
  const setLastFocusedId = useSetAtom(
    lastFocusedPushToTalkScopeAtom,
    APP_STORE,
  );

  useEffect(
    () => () =>
      setLastFocusedId((current) => (current === id ? null : current)),
    [id, setLastFocusedId],
  );

  const isAllowedTarget = (target: EventTarget | null): boolean => {
    const own = ref.current;
    if (!(enabled && own)) return false;
    if (target instanceof Node && own.contains(target)) return true;
    if (isEditableTarget(target)) return false;

    const scopes = Array.from(
      document.querySelectorAll(`[${SCOPE_ATTRIBUTE}]`),
    ).filter(isShown);
    const owner =
      scopes.find((el) => el.getAttribute(SCOPE_ATTRIBUTE) === lastFocusedId) ??
      scopes[0];
    return owner === own;
  };

  return {
    isAllowedTarget,
    scopeProps: {
      [SCOPE_ATTRIBUTE]: enabled ? id : undefined,
      onFocusCapture: () => setLastFocusedId(id),
    },
  };
};
